import React, { useState, useEffect, useCallback } from 'react';
import { Bot, RefreshCw, Link2, Unlink, RotateCcw, AlertTriangle, CheckCircle2, Phone } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import ConfirmationModal from './ConfirmationModal';

/**
 * Superadmin → Add-ons → Phone Agent: link an assistant the Gain Rhino
 * team built on the voice platform to this org. Three states: link form
 * (no agent / numberless draft), "reset first" (active CRM-built agent),
 * linked (re-sync / unlink).
 */
const PlatformAssistantCard = ({ orgId, orgName }) => {
  const [status, setStatus] = useState(null); // linkStatus payload
  const [loading, setLoading] = useState(true);
  const [assistants, setAssistants] = useState([]);
  const [squads, setSquads] = useState([]);
  const [numbers, setNumbers] = useState([]);
  const [listsLoading, setListsLoading] = useState(false);
  const [assistantId, setAssistantId] = useState('');
  const [numberChoice, setNumberChoice] = useState('org'); // 'org' | vapi number id
  const [busy, setBusy] = useState(null); // 'link' | 'sync' | 'unlink' | 'reset'
  const [confirm, setConfirm] = useState(null); // 'unlink' | 'reset'
  const [warnings, setWarnings] = useState([]);
  const [hookUrl, setHookUrl] = useState('');
  const [hookEnabled, setHookEnabled] = useState(false);
  const [hookSaving, setHookSaving] = useState(false);
  const [provider, setProvider] = useState('vapi');
  const [gptVoice, setGptVoice] = useState('marin');
  const [consentOn, setConsentOn] = useState(true);
  const [consentText, setConsentText] = useState('');
  const [providerSaving, setProviderSaving] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/superadmin/organizations/${orgId}/phone-agent`);
      setStatus(data);
      setWarnings(data.warnings || []);
      setHookUrl(data.postCallWebhook?.url || '');
      setHookEnabled(!!data.postCallWebhook?.enabled);
      setProvider(data.voiceProvider || 'vapi');
      setGptVoice(data.gptliveVoice || 'marin');
      setConsentOn(data.recordingConsent ? data.recordingConsent.enabled !== false : true);
      setConsentText(data.recordingConsent?.text || '');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load Phone Agent state');
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  const loadLists = useCallback(async () => {
    setListsLoading(true);
    try {
      const [a, n, sq] = await Promise.all([
        api.get('/superadmin/phone-agent/platform-assistants'),
        api.get('/superadmin/phone-agent/platform-numbers'),
        api.get('/superadmin/phone-agent/platform-squads').catch(() => ({ data: { squads: [] } })),
      ]);
      setAssistants(a.data.assistants || []);
      setNumbers(n.data.numbers || []);
      setSquads(sq.data.squads || []);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load platform assistants');
    } finally {
      setListsLoading(false);
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const canLink = status && (!status.exists || (status.managedBy === 'crm' && !status.hasAssistant && !status.hasNumber));
  const needsReset = status?.exists && status.managedBy === 'crm' && (status.hasAssistant || status.hasNumber);
  const linked = status?.exists && status.managedBy === 'platform';
  const isGpt = status?.exists && status.voiceProvider === 'gptlive';

  useEffect(() => {
    if (canLink && assistants.length === 0 && !listsLoading) loadLists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canLink]);

  // Pre-select the number attached to the chosen assistant on the platform.
  useEffect(() => {
    const a = assistants.find((x) => x.id === assistantId);
    if (a?.attachedNumberId && numbers.some((n) => n.id === a.attachedNumberId)) {
      setNumberChoice(a.attachedNumberId);
    } else {
      setNumberChoice('org');
    }
  }, [assistantId, assistants, numbers]);

  const act = async (kind, path, body) => {
    setBusy(kind);
    try {
      const { data } = await api.post(`/superadmin/organizations/${orgId}/phone-agent/${path}`, body || {});
      setStatus(data.status);
      setWarnings(data.warnings || data.status?.warnings || []);
      return data;
    } catch (err) {
      toast.error(err.response?.data?.error || `Failed to ${kind}`);
      await loadStatus();
      return null;
    } finally {
      setBusy(null);
      setConfirm(null);
    }
  };

  const isSquadChoice = assistantId.startsWith('squad:');
  const doLink = async () => {
    if (!assistantId) { toast.error('Pick an assistant first'); return; }
    const data = await act('link', 'link', {
      ...(isSquadChoice ? { squadId: assistantId.slice(6) } : { assistantId }),
      ...(numberChoice !== 'org' ? { phoneNumberId: numberChoice } : {}),
    });
    if (data) {
      toast.success(numberChoice !== 'org' ? 'Assistant linked and live' : 'Assistant linked — the org picks a number in its wizard');
      setAssistantId('');
    }
  };

  const saveHook = async () => {
    setHookSaving(true);
    try {
      const { data } = await api.put(`/superadmin/organizations/${orgId}/phone-agent/webhook`, { url: hookUrl.trim(), enabled: hookEnabled });
      setStatus(data.status);
      toast.success(hookEnabled ? 'Post-call webhook enabled' : 'Post-call webhook saved (disabled)');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save webhook');
    } finally {
      setHookSaving(false);
    }
  };

  const saveProvider = async () => {
    setProviderSaving(true);
    try {
      const { data } = await api.put(`/superadmin/organizations/${orgId}/phone-agent/provider`, {
        voiceProvider: provider,
        gptliveVoice: gptVoice.trim() || 'marin',
        recordingConsent: { enabled: consentOn, text: consentText.trim() },
      });
      setStatus(data.status);
      toast.success(provider === 'gptlive'
        ? 'Voice provider set to GPT-Live. Activate / Save & Republish in the org\'s Phone Agent settings to re-point the number.'
        : 'Voice provider set to VAPI. Re-provision the agent to move the number back.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save provider');
    } finally {
      setProviderSaving(false);
    }
  };
  const selectedAssistant = assistants.find((x) => x.id === assistantId);
  const chosenNumberObj = numbers.find((n) => n.id === numberChoice);
  const missingCallerContext = warnings.includes('no_caller_context')
    || (!!selectedAssistant && selectedAssistant.warnings?.includes('no_caller_context'));

  const CallerContextHint = () => (
    <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-800 flex items-start gap-2">
      <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
      <span>
        The assistant's system prompt does not contain <code className="font-mono">{'{{callerContext}}'}</code>. Add it on the platform so callers are greeted by name; everything else (lead capture, appointments, call logs) works without it.
      </span>
    </div>
  );

  const webhookSection = (status?.exists && (
          <div className="mt-3 pt-3 border-t border-teal-200/70">
            <div className="text-xs font-medium text-teal-900 mb-1">Post-call webhook</div>
            <p className="text-xs text-teal-800 mb-2">After every call, POST a JSON payload with a caller-facing recap message, the collected intake fields, the summary and the CRM lead link. Signed with <code className="font-mono">X-Signature</code> (HMAC-SHA256 of the body, key = this agent's webhook secret).</p>
            <div className="flex flex-wrap items-center gap-2">
              <input type="url" value={hookUrl} onChange={(e) => setHookUrl(e.target.value)} placeholder="https://…/webhook/…"
                className="flex-1 min-w-[240px] border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:ring-teal-500 focus:border-teal-500 bg-white" />
              <label className="flex items-center gap-1.5 text-xs text-gray-700">
                <input type="checkbox" className="h-4 w-4 text-teal-600 rounded" checked={hookEnabled} onChange={(e) => setHookEnabled(e.target.checked)} /> Enabled
              </label>
              <button type="button" onClick={saveHook} disabled={hookSaving}
                className="px-3 py-1.5 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50 text-xs font-medium">
                {hookSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
            {status.postCallWebhook?.secret && (
              <div className="text-xs text-teal-900 mt-1.5 flex items-center gap-2">
                <span>Signing secret:</span>
                <code className="font-mono bg-white border border-teal-200 rounded px-1.5 py-0.5">{status.postCallWebhook.secret.slice(0, 6)}…{status.postCallWebhook.secret.slice(-4)}</code>
                <button type="button" onClick={() => { navigator.clipboard?.writeText(status.postCallWebhook.secret); toast.success('Secret copied'); }}
                  className="px-2 py-0.5 bg-white border border-teal-300 text-teal-800 rounded hover:bg-teal-50">Copy</button>
              </div>
            )}
            {status.postCallWebhook?.lastSentAt && (
              <div className={`text-xs mt-1.5 ${status.postCallWebhook.lastError ? 'text-red-700' : 'text-teal-800'}`}>
                Last delivery {new Date(status.postCallWebhook.lastSentAt).toLocaleString()} · HTTP {status.postCallWebhook.lastStatus || 'n/a'}{status.postCallWebhook.lastError ? ` · ${status.postCallWebhook.lastError}` : ' · ok'}
              </div>
            )}
          </div>
  ));

  return (
    <div className="mt-3 p-4 rounded-lg border border-gray-200 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <Bot size={16} className="text-teal-600" />
          <span className="font-medium text-gray-900 text-sm">Platform assistant</span>
        </div>
        <button type="button" onClick={loadStatus} disabled={loading}
          className="text-xs text-gray-500 hover:text-gray-800 flex items-center gap-1">
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        Link an assistant built directly on the voice platform. The CRM adopts its server config and adds its four function tools; the script, voice and model stay as built.
      </p>
      <div className="mb-3 p-3 rounded-md border border-indigo-200 bg-indigo-50/60">
        <div className="text-xs font-medium text-indigo-900 mb-1">Voice provider</div>
        <p className="text-xs text-indigo-800 mb-2">
          Which platform carries the audio. <span className="font-medium">GPT-Live</span> runs on the Gain Rhino voice gateway (OpenAI GPT-Live-1, ~half the per-minute cost, full-duplex); the wizard, tools, call logs and webhooks stay exactly the same. Switching takes effect when the org next activates or republishes its agent.
          {status?.gatewayConfigured === false && <span className="text-red-700"> Gateway is not configured on this server (GATEWAY_URL).</span>}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <select value={provider} onChange={(e) => setProvider(e.target.value)}
            className="border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white focus:ring-indigo-500 focus:border-indigo-500">
            <option value="vapi">VAPI (default)</option>
            <option value="gptlive" disabled={status?.gatewayConfigured === false}>GPT-Live gateway</option>
          </select>
          {provider === 'gptlive' && (
            <>
              <select value={gptVoice} onChange={(e) => setGptVoice(e.target.value)} title="OpenAI voice"
                className="border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white focus:ring-indigo-500 focus:border-indigo-500">
                {[['gleam', 'Gleam — female, North American (natural)'], ['meridian', 'Meridian — male, North American (natural)'], ['willow', 'Willow — female, Irish (natural)'], ['stone', 'Stone — male, Irish (natural)'], ['vesper', 'Vesper — male, British (natural)'], ['ripple', 'Ripple — male, Australian (natural)'], ['bossa', 'Bossa — female, Brazilian Portuguese (natural)'], ['tempo', 'Tempo — male, Brazilian Portuguese (natural)'], ['quartz', 'Quartz — female, Australian (generated)'], ['delta', 'Delta — female, Southern US (generated)'], ['cinder', 'Cinder — male, Southern US (generated)'], ['beacon', 'Beacon — male, Filipino English (generated)'], ['marin', 'Marin — female (legacy Realtime voice)'], ['cedar', 'Cedar — male (legacy Realtime voice)']].map(([v, label]) => (
                  <option key={v} value={v}>{label}</option>
                ))}
              </select>
              <label className="flex items-center gap-1.5 text-xs text-gray-700">
                <input type="checkbox" className="h-4 w-4 text-indigo-600 rounded" checked={consentOn} onChange={(e) => setConsentOn(e.target.checked)} /> Recording notice
              </label>
              <input type="text" value={consentText} onChange={(e) => setConsentText(e.target.value)} placeholder="Notice text (blank = default for the agent's language)"
                className="flex-1 min-w-[240px] border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white" />
            </>
          )}
          <button type="button" onClick={saveProvider} disabled={providerSaving}
            className="px-3 py-1.5 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 text-xs font-medium">
            {providerSaving ? 'Saving…' : 'Save provider'}
          </button>
        </div>
        {status?.exists && status.voiceProvider === 'gptlive' && (
          <div className="text-xs text-indigo-900 mt-1.5">Active provider: GPT-Live gateway{status.phoneNumber ? <> on <span className="font-mono">{status.phoneNumber}</span></> : ''} · status {status.status}</div>
        )}
      </div>

      {loading && !status && (
        <div className="text-sm text-gray-500 flex items-center gap-2"><RefreshCw size={14} className="animate-spin" /> Loading…</div>
      )}

      {isGpt && (
        <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-md">
          <div className="text-sm text-indigo-900 flex items-center gap-2">
            {status.status === 'active' ? <CheckCircle2 size={16} className="text-indigo-600" /> : <AlertTriangle size={16} className="text-amber-600" />}
            <span>GPT-Live gateway agent{status.phoneNumber ? <> on <span className="font-mono">{status.phoneNumber}</span></> : ''} · status {status.status}</span>
          </div>
          {webhookSection}
        </div>
      )}
      {!isGpt && needsReset && (
        <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
          <div className="text-sm text-gray-800 flex items-start gap-2">
            <AlertTriangle size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-medium">This org already has a CRM-built agent{status.phoneNumber ? <> on <span className="font-mono">{status.phoneNumber}</span></> : ''}.</div>
              <div className="text-xs text-gray-600 mt-1">Reset it to remove the assistant and number registration from the platform. The purchased number stays on the account, and the org's config, knowledgebase and call history are kept.</div>
            </div>
          </div>
          <button type="button" onClick={() => setConfirm('reset')} disabled={!!busy}
            className="mt-3 px-3 py-1.5 bg-white border border-red-300 text-red-700 rounded-md hover:bg-red-50 disabled:opacity-50 text-sm font-medium flex items-center gap-1.5">
            <RotateCcw size={14} /> Reset agent
          </button>
        </div>
      )}

      {!isGpt && canLink && (
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Assistant</label>
            <div className="flex flex-wrap items-center gap-2">
              <select value={assistantId} onChange={(e) => setAssistantId(e.target.value)}
                className="flex-1 min-w-[220px] border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-teal-500 focus:border-teal-500">
                <option value="">— Select an assistant or squad —</option>
                {squads.length > 0 && (
                  <optgroup label="Squads (multi-assistant, e.g. one per language)">
                    {squads.map((sq) => (
                      <option key={sq.id} value={`squad:${sq.id}`} disabled={!!sq.inUseBy}>
                        {sq.name} · {sq.members.map((m) => m.name).join(', ')}{sq.inUseBy ? ` (in use by ${sq.inUseBy})` : ''}
                      </option>
                    ))}
                  </optgroup>
                )}
                <optgroup label="Single assistants">
                  {assistants.map((a) => (
                    <option key={a.id} value={a.id} disabled={!!a.inUseBy}>
                      {a.name}{a.attachedNumber ? ` · ${a.attachedNumber}` : ''}{a.inUseBy ? ` (in use by ${a.inUseBy})` : ''}
                    </option>
                  ))}
                </optgroup>
              </select>
              <button type="button" onClick={loadLists} disabled={listsLoading}
                className="px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:border-gray-400 disabled:opacity-50 text-xs font-medium flex items-center gap-1.5">
                <RefreshCw size={12} className={listsLoading ? 'animate-spin' : ''} /> Reload
              </button>
            </div>
          </div>

          {assistantId && (
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Phone number</label>
              <div className="space-y-1.5">
                <label className={`flex items-center gap-2 p-2.5 rounded-md border cursor-pointer text-sm ${numberChoice === 'org' ? 'border-teal-500 bg-teal-50 ring-1 ring-teal-500' : 'border-gray-200 hover:border-gray-300'}`}>
                  <input type="radio" name="platformNumber" className="h-4 w-4 text-teal-600" checked={numberChoice === 'org'} onChange={() => setNumberChoice('org')} />
                  <span>Let the org pick a number in its wizard (buy new or use an owned number)</span>
                </label>
                {numbers.map((n) => (
                  <label key={n.id} className={`flex items-center gap-2 p-2.5 rounded-md border cursor-pointer text-sm ${numberChoice === n.id ? 'border-teal-500 bg-teal-50 ring-1 ring-teal-500' : 'border-gray-200 hover:border-gray-300'}`}>
                    <input type="radio" name="platformNumber" className="h-4 w-4 text-teal-600" checked={numberChoice === n.id} onChange={() => setNumberChoice(n.id)} />
                    <Phone size={14} className="text-gray-400" />
                    <span className="font-mono">{n.number || n.name || n.id}</span>
                    {n.assistantId === assistantId && <span className="text-xs text-teal-700">attached to this assistant</span>}
                    {n.name && n.assistantId !== assistantId && <span className="text-xs text-gray-500 truncate">{n.name}</span>}
                  </label>
                ))}
                {numbers.length === 0 && !listsLoading && (
                  <div className="text-xs text-gray-500">No unattached platform numbers. The org can pick one in its wizard.</div>
                )}
              </div>
            </div>
          )}

          {selectedAssistant && missingCallerContext && <CallerContextHint />}

          <button type="button" onClick={doLink} disabled={!assistantId || busy === 'link'}
            className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 disabled:opacity-50 text-sm font-medium flex items-center gap-2">
            {busy === 'link' ? <RefreshCw size={14} className="animate-spin" /> : <Link2 size={14} />}
            {busy === 'link' ? 'Linking…' : chosenNumberObj ? 'Link & go live' : 'Link assistant'}
          </button>
        </div>
      )}

      {!isGpt && linked && (
        <div className="p-3 bg-teal-50 border border-teal-200 rounded-md">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="text-sm">
              <div className="font-medium text-teal-900 flex items-center gap-2">
                {status.status === 'active'
                  ? <CheckCircle2 size={16} className="text-teal-600" />
                  : <AlertTriangle size={16} className={status.status === 'error' ? 'text-red-600' : 'text-amber-600'} />}
                {status.assistantName || status.agentName || 'Linked assistant'}
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  status.status === 'active' ? 'bg-green-100 text-green-800'
                    : status.status === 'error' ? 'bg-red-100 text-red-800'
                      : 'bg-gray-100 text-gray-700'}`}>
                  {status.status}
                </span>
              </div>
              {status.squad && (
                <div className="text-xs text-teal-800 mt-1">Squad members: {status.squad.members.join(' → ')}</div>
              )}
              <div className="text-xs text-teal-800 mt-1">
                {status.phoneNumber
                  ? <>Number <span className="font-mono">{status.phoneNumber}</span></>
                  : 'No number yet — the org picks one in its wizard'}
                {status.linkedAt && <> · linked {new Date(status.linkedAt).toLocaleDateString()}{status.linkedBy?.name ? ` by ${status.linkedBy.name}` : ''}</>}
              </div>
              {status.assistantMissing && (
                <div className="text-xs text-red-700 mt-1">The assistant no longer exists on the platform. Unlink and link another.</div>
              )}
              {status.status === 'error' && status.lastError && (
                <div className="text-xs text-red-700 mt-1">Last error: {status.lastError}</div>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => act('sync', 'sync').then((d) => d && toast.success('Assistant re-synced'))}
                disabled={!!busy || status.assistantMissing}
                className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-md hover:border-gray-400 disabled:opacity-50 text-xs font-medium flex items-center gap-1.5">
                <RefreshCw size={12} className={busy === 'sync' ? 'animate-spin' : ''} /> Re-sync
              </button>
              <button type="button" onClick={() => setConfirm('unlink')} disabled={!!busy}
                className="px-3 py-1.5 bg-white border border-red-300 text-red-700 rounded-md hover:bg-red-50 disabled:opacity-50 text-xs font-medium flex items-center gap-1.5">
                <Unlink size={12} /> Unlink
              </button>
            </div>
          </div>
          {missingCallerContext && <CallerContextHint />}

          {webhookSection}
        </div>
      )}

      <ConfirmationModal
        isOpen={confirm === 'reset'}
        onClose={() => setConfirm(null)}
        onConfirm={() => act('reset', 'reset').then((d) => d && toast.success('Agent reset'))}
        title="Reset this org's Phone Agent?"
        message={`This deletes the CRM-built assistant and the number registration on the voice platform for ${orgName}. ${status?.phoneNumber ? `The number ${status.phoneNumber} stays on the account and can be re-attached. ` : ''}Config, knowledgebase and call history are kept.`}
        confirmText="Reset agent"
        isLoading={busy === 'reset'}
      />
      <ConfirmationModal
        isOpen={confirm === 'unlink'}
        onClose={() => setConfirm(null)}
        onConfirm={() => act('unlink', 'unlink').then((d) => d && toast.success('Assistant unlinked'))}
        title="Unlink the platform assistant?"
        message={`The CRM tools and webhook config are removed from the assistant and its number; nothing on the platform is deleted. ${orgName}'s agent goes back to an empty draft.`}
        confirmText="Unlink"
        isLoading={busy === 'unlink'}
      />
    </div>
  );
};

export default PlatformAssistantCard;
